import { useState, useEffect } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import {
  SearchIcon,
  PlusIcon,
  AlertCircleIcon,
  MessageCircle,
  FileText,
  LayoutList,
} from "lucide-react";
import { getMyAssessments } from "@service/assessment";
import { Assessment } from "@type/assessments";
import { AssessmentFilterState, JobSortState } from "@type/job";
import SortPanelDropdown from "@component/job-sort-filter";
import LoadingBox from "@component/loading-box";
import AssessmentCard from "@component/assessment-card";
import AssessmentFilterPanel from "@component/assessment-filter";
import { defaultPagination } from "@constant/app";
import { MetaPagination } from "@type/common";
import Pagination from "@component/pagination";
import { catchErrorLog } from "@util/sentry";
import { AssessmentCreator } from "./employer-assessment-creator";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { EmptyState } from "@component/empty-state";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";

export function AssessmentSection() {
  const [, userInfo] = useUserProfileContext();
  // Create Assessment is blocked once the workspace is restricted (trial
  // expired + past the 7-day grace) or the trial AI-assessment quota is
  // exhausted — see subscription_context `isAssessmentActionBlocked`.
  const { isAssessmentActionBlocked } = useSubscription();
  const [isCreatingAssessment, setIsCreatingAssessment] = useState(false);
  const [filters, setFilters] = useState<AssessmentFilterState>({
    status: [
      "Active",
      "Draft",
    ],
  });
  const [sortOptions, setSortOptions] = useState<JobSortState>({
    job_date: "",
    job_title: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(defaultPagination.page);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [paginationMetadata, setPaginationMetadata] = useState<MetaPagination>({
    page: defaultPagination.page,
    limit: 12,
    total: 0,
    total_pages: 0
  })

  useEffect(() => {
    const savedSortOptions = localStorage.getItem("assessmentSortOptions") ? JSON.parse(localStorage.getItem("assessmentSortOptions") || '{}') : { job_date: "", job_title: "" };
    const savedFilters = localStorage.getItem("assessmentFilters") ? JSON.parse(localStorage.getItem("assessmentFilters") || '{}') : {
      status: [
        "Active",
        "Draft",
      ]
    };
    setFilters(savedFilters);
    setSortOptions(savedSortOptions);
    fetchAssessments("", 1, "all", savedFilters.status, savedSortOptions.job_date);
  }, [])

  const fetchAssessments = async (keyword: string, page: number, filterType: string, filterStatus?: string[], sort?: string) => {
    setIsLoading(true);
    setError(null);

    getMyAssessments({
      page,
      limit: 12,
      keyword: searchQuery || "",
      assess_type: filterType,
      assess_status: filterStatus?.join(",") || undefined,
      sort: sort || undefined,
    }).then((resp) => {
      if (resp.status === 200) {
        setAssessments(resp.data.data);
        setPaginationMetadata(resp.data.meta.pagination);
      }
    }).catch((err) => {
      catchErrorLog("Error fetching assessments:", err);
      setError("An unexpected error occurred");
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const onSearchQueryChange = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    fetchAssessments(searchQuery, 1, activeTab, filters.status, sortOptions.job_date);
  }

  const onFiltersChange = (newFilters: AssessmentFilterState) => {
    setFilters(newFilters);
    fetchAssessments(searchQuery, 1, activeTab, newFilters.status, sortOptions.job_date);
  }

  const onSortChange = (newSortOptions: JobSortState) => {
    setSortOptions(newSortOptions);
    fetchAssessments(searchQuery, 1, activeTab, filters.status, newSortOptions.job_date);
  }

  const onLoadPagination = (page: number) => {
    setCurrentPage(page);
    fetchAssessments(searchQuery, page, activeTab, filters.status, sortOptions.job_date);
  }

  const onReload = () => {
    fetchAssessments(searchQuery, currentPage, activeTab, filters.status, sortOptions.job_date);
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update filters based on selected tab
    fetchAssessments(searchQuery, currentPage, value, filters.status, sortOptions.job_date);
  };

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") {
      fetchAssessments("", 1, activeTab, filters.status, sortOptions.job_date);
    }
  }

  // If creating assessment, show the creator component
  if (isCreatingAssessment) {
    return (
      <div>
        <AssessmentCreator
          onClose={() => {
            setIsCreatingAssessment(false);
            onReload();
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex items-center justify-between mb-4 gap-2 min-w-0">
          <TabsList className="flex w-auto bg-transparent h-auto p-0 border-b border-gray-200 justify-start items-center rounded-none overflow-x-auto scrollbar-hide">
            <TabsTrigger
              value="all"
              className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              <LayoutList className="h-4 w-4 mr-2" />
              All Screenings
            </TabsTrigger>
            <TabsTrigger
              value="Interview"
              className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              <MessageCircle className="h-4 w-4" />
              AI Interviews
            </TabsTrigger>
            <TabsTrigger
              value="Form"
              className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              <FileText className="h-4 w-4" />
              Forms
            </TabsTrigger>
          </TabsList>

          {userInfo?.permission !== "member" && (
            <Button
              size="sm"
              className="flex items-center gap-1 flex-shrink-0"
              onClick={() => setIsCreatingAssessment(true)}
              disabled={isAssessmentActionBlocked}
              title={isAssessmentActionBlocked ? "Your trial has expired or AI screening limit reached. Upgrade to create new screenings." : undefined}
            >
              <PlusIcon className="h-4 w-4" />
              Create Screening
            </Button>
          )}
        </div>
      </Tabs>

      <div className="mb-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <form onSubmit={onSearchQueryChange} className="relative flex-grow flex-1">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search screenings..."
              className="pl-10"
              value={searchQuery}
              onChange={onSearchInputChange}
            />
          </form>
        </div>
        <div className="flex gap-2">
          <AssessmentFilterPanel
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
          <SortPanelDropdown disabledAlpha optionName="assessmentSortOptions" sortOptions={sortOptions} onSortChange={onSortChange} />
        </div>
      </div>

      {isLoading ? (
        <LoadingBox />
      ) : error ? (
        <div className="text-center py-10">
          <AlertCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 mb-2">Error loading screenings</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <Button
            onClick={onReload}
            variant="outline"
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assessments.map((assessment, index) => (
              <AssessmentCard
                key={assessment.id || `assessment-${index}`}
                assessment={assessment}
                onReload={onReload}
              />
            ))}
          </div>

          {!isLoading && !error && assessments.length === 0 && (
            <EmptyState
              icon={(
                <SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              )}
              title="No screenings available"
              description="Adjust your filters or create a new screening to get started."
            />
          )}

          <div className="p-4">
            <Pagination
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={paginationMetadata.total_pages || 0}
              onPageChange={onLoadPagination}
            />
          </div>
        </>
      )}
    </div>
  );
}
